import { ConfirmationModalBase } from "@/components/common/modals";
// import { useGetVariables } from "@/workflow_api/hooks/variables/useGetVariables";
import { useUIStore } from "@/workflow_api/store/uiStore";
import { useWorkflowStore } from "@/workflow_api/store/workflowStore";
import {
  NodeTypes,
  type NodeData,
  type RefName,
} from "@/workflow_api/types/workflow";
import { useRef, useState } from "react";
import FullModal from "../../full-modal";
import ThreeColumnLayout from "@/components/common/three-column-layout";
import Variables from "@/workflow_api/components/variables";
import { Icon } from "@elaniintc/goes-design-system";
import Empty from "@/workflow_api/components/variables/empty";
import { useResetNode, useUpdateNode } from "@/workflow_api/services/nodes.api";
import { useGetN8NAvailableFlows, useGetN8NFlowSchema } from "@/queries/n8n";
import ServiceSelect from "./components/ServiceSelect";
import ServiceInputs from "./components/ServiceInputs";
import { InputOutput } from "../../InputOutput";
import type { UseFormReturn } from "react-hook-form";
import serializer from "./serializer";
import { useGetVariables } from "@/workflow_api/hooks/variables/useGetVariables";
import { useNodeNameValidation } from "@/workflow_api/hooks/useNodeNameValidation";

const N8NDetails = () => {
  const {
    selectedNodeId,
    nodeDetailsOpen,
    setNodeDetailsOpen,
    setSelectedNodeId,
  } = useUIStore();
  const { getNodeById, flowId } = useWorkflowStore();
  const node = getNodeById(selectedNodeId || "");
  const nodeData = node?.data as unknown as NodeData;
  const [title, setTitle] = useState<string>(nodeData.displayName || "");
  const [modalState, setModalState] = useState<"idle" | "close-warning">(
    "idle",
  );
  const [showNodeTitleValidation, setShowNodeTitleValidation] = useState(false);
  const { getNameValidationError, isNameValid } =
    useNodeNameValidation(selectedNodeId);
  const [manualConfirmation, setManualConfirmation] = useState(() => {
    return nodeData?.manual_continue ?? false;
  });

  const { variables: nodeVariables, isLoading: isLoadingVariables } =
    useGetVariables();

  const { mutateAsync, isPending: isLoadingSave } = useUpdateNode(flowId);
  const { mutateAsync: resetNode, isPending: isLoadingReset } = useResetNode();
  const { data: n8nFlows, isFetching } = useGetN8NAvailableFlows();

  const defaultSelectedService = nodeData.inputs.find(
    (i) => i.name === "workflow",
  )?.value as string;
  const [inputs, setInputs] = useState<NodeData["inputs"]>(
    nodeData.inputs || [],
  );

  const [selectedService, setSelectedService] = useState<string>(() => {
    const input = defaultSelectedService;
    return (input as string) || "";
  });
  const {
    data: flowSchema,
    isPending,
    error,
    refetch,
    isRefetching,
  } = useGetN8NFlowSchema(selectedService);

  const formRef = useRef<UseFormReturn>(null);

  const onSaveNode = async () => {
    const methods = formRef.current;
    if (!methods) return;
    const isValid = await methods.trigger();
    if (!isValid) return;
    const inputs = serializer(flowSchema!, methods.getValues(), nodeVariables);
    console.log(inputs);
    if (!node) return;
    const nodeData = node.data as unknown as NodeData;
    await resetNode({
      node_id: node.id,
      node: {
        flow_id: flowId,
        ref_name: nodeData.type as RefName,
        display_name: title,
        order: nodeData.order,
        position_x: node.position.x,
        position_y: node.position.y,
        type: nodeData.node_type,
      },
    });
    await mutateAsync({
      node_id: node.id,
      node: {
        flow_id: flowId,
        ref_name: nodeData.type as RefName,
        display_name: title,
        order: nodeData.order,
        position_x: node.position.x,
        position_y: node.position.y,
        type: "N8N",
        manual_continue: manualConfirmation,
        inputs: [
          {
            name: "workflow",
            type: "STRING",
            value: selectedService,
          },
          ...inputs,
        ],
      },
    });

    setNodeDetailsOpen(false);
    setSelectedNodeId(undefined);
  };

  const isLoading = false;

  return (
    <>
      <ConfirmationModalBase
        isOpen={modalState === "close-warning"}
        acceptText="Salir sin guardar"
        cancelText="Cancelar"
        description="¿Estás seguro de que deseas salir? Todos los cambios que hayas realizado se perderán"
        title="Salir sin guardar"
        acceptVariant="danger-secondary"
        isLoading={false}
        onAccept={() => {
          setNodeDetailsOpen(false);
          setSelectedNodeId();
          setModalState("idle");
        }}
        onClose={() => {
          setModalState("idle");
        }}
      />
      <FullModal
        isOpen={nodeDetailsOpen}
        onClose={() => {
          setModalState("close-warning");
        }}
        isSaveValid={isNameValid(title)}
        isLoading={isLoadingSave || isLoadingReset}
        onSave={() => {
          setShowNodeTitleValidation(true);
          if (!isNameValid(title)) return;
          onSaveNode();
        }}
      >
        <ThreeColumnLayout
          leftHeader={{
            icon: "arrow-right-to-bracket",
            title: "Inputs",
            color: "neutral",
          }}
          centerHeader={{
            icon: "hexagon-nodes",
            title: title,
            color: "warning",
            editable: true,
            onTitleChange: (newTitle: string) => {
              setTitle(newTitle);
              if (newTitle.trim() === "") {
                setShowNodeTitleValidation(true);
              }
            },
            onTitleBlur: () => setShowNodeTitleValidation(true),
            validationError: getNameValidationError(title),
            showValidation: showNodeTitleValidation,
            manualConfirmation,
            onManualConfirmationChange: setManualConfirmation,
          }}
          rightHeader={{
            icon: "arrow-right-from-bracket",
            title: "Outputs",
            color: "neutral",
          }}
          leftContent={
            <div className="bg-neutral-foreground overflow-auto divide-y divide-[var(--border-neutral-quiet)]">
              <Variables sourceNodeId={selectedNodeId!} />
            </div>
          }
          centerContent={
            <>
              {isLoading && (
                <div className="w-full flex items-center justify-center min-h-[300px]">
                  <Icon
                    name="spinner-third"
                    className="animate-spin text-on-disabled"
                    type="far"
                  />
                </div>
              )}
              {!isLoading && (
                <div className="flex flex-col">
                  <ServiceSelect
                    isLoading={isFetching}
                    options={n8nFlows?.data || []}
                    selectedService={selectedService}
                    onServiceChange={(service) => {
                      setSelectedService(service);
                      if (service !== selectedService) {
                        setInputs([]);
                      }
                    }}
                  />
                  {selectedService && (
                    <ServiceInputs
                      key={flowSchema ? flowSchema.workflow : "no-schema"}
                      ref={formRef}
                      defaultData={inputs}
                      isLoading={
                        isPending || isRefetching || isLoadingVariables
                      }
                      selectedService={selectedService}
                      error={Boolean(error)}
                      data={flowSchema!}
                      retry={refetch}
                      variables={nodeVariables}
                      isLoadingVariables={isLoadingVariables}
                    />
                  )}
                </div>
              )}
            </>
          }
          rightContent={
            <>
              {flowSchema && (
                <>
                  {flowSchema?.outputs?.length === 0 ? (
                    <Empty />
                  ) : (
                    <div className="bg-neutral-foreground overflow-auto divide-y divide-[var(--border-neutral-quiet)]">
                      <InputOutput
                        isLoading={isPending || isRefetching}
                        nodeLabel="objeto-n8n"
                        nodeType={NodeTypes.N8N}
                        showSteps={false}
                        variables={flowSchema?.outputs
                          ?.map((output) => {
                            return Object.keys(output).map((key) => ({
                              name: key,
                              type: output[key].type,
                            }));
                          })
                          .flat()}
                      />
                    </div>
                  )}
                </>
              )}
            </>
          }
        />
      </FullModal>
    </>
  );
};
export default N8NDetails;
